/**
 * Tavily Search API Service
 * Handles web search for research agent
 *
 * Primary path: Supabase Edge Function (keeps API key server-side)
 * Fallback path: Direct Tavily API using client env (if function unavailable)
 */

import axios, { AxiosError } from 'axios';
import { ENV } from '@/config/env';
import { supabase } from '@services/supabase';
import {
  TavilyResponse,
  TavilySearchResult,
  ResearchSource,
  ResearchServiceResponse,
} from '@/types/research.types';

class TavilyService {
  private readonly apiUrl = 'https://api.tavily.com';
  private readonly apiKey: string;
  private readonly timeout = 30000; // 30 seconds

  // Rate limiting
  private requestCount = 0;
  private resetTime = Date.now() + 60000; // Reset every minute

  constructor() {
    this.apiKey = ENV.TAVILY_API_KEY || '';
  }

  /**
   * Validate API key exists (used only for direct fallback path)
   */
  private validateApiKey(): void {
    if (!this.apiKey) {
      throw new Error('Tavily API key is missing. Please set TAVILY_API_KEY in your environment.');
    }
  }

  /**
   * Rate limiting check (simple implementation)
   * Only applied for direct fallback requests
   */
  private checkRateLimit(): void {
    const now = Date.now();
    if (now > this.resetTime) {
      this.requestCount = 0;
      this.resetTime = now + 60000;
    }

    if (this.requestCount >= 50) {
      throw new Error('Rate limit exceeded. Please try again in a moment.');
    }

    this.requestCount++;
  }

  /**
   * Call Supabase Edge Function (preferred path)
   */
  private async invokeEdgeFunction<T = any>(
    body: Record<string, any>
  ): Promise<ResearchServiceResponse<T>> {
    try {
      const { data, error } = await supabase.functions.invoke('tavily-search', { body });
      if (error) {
        return { data: null as any, error: new Error(error.message || 'Edge function error') };
      }
      return { data: (data?.data as T) ?? (null as any), error: null };
    } catch (err) {
      return {
        data: null as any,
        error: err instanceof Error ? err : new Error('Unknown edge function error'),
      };
    }
  }

  /**
   * Search the web using Tavily API
   *
   * @param query - Search query
   * @param options - Search options
   * @returns Array of search results
   */
  async search(
    query: string,
    options?: {
      maxResults?: number;
      searchDepth?: 'basic' | 'advanced'; // Advanced does deeper crawling
      includeDomains?: string[];
      excludeDomains?: string[];
    }
  ): Promise<ResearchServiceResponse<TavilySearchResult[]>> {
    try {
      // Preferred: call Supabase Edge Function (server-side secret)
      const edgeRes = await this.invokeEdgeFunction<TavilySearchResult[]>({
        mode: 'single',
        query,
        options: {
          maxResults: options?.maxResults ?? 5,
          searchDepth: options?.searchDepth ?? 'basic',
          includeDomains: options?.includeDomains ?? [],
          excludeDomains: options?.excludeDomains ?? [],
        },
      });
      if (!edgeRes.error && edgeRes.data) {
        return { data: edgeRes.data, error: null };
      }

      // Fallback: direct API call if edge function unavailable
      this.validateApiKey();
      this.checkRateLimit();

      const payload = {
        api_key: this.apiKey,
        query,
        search_depth: options?.searchDepth || 'basic',
        max_results: options?.maxResults || 5,
        include_answer: false,
        include_raw_content: false,
        include_images: false,
        include_domains: options?.includeDomains || [],
        exclude_domains: options?.excludeDomains || [],
      };

      const response = await axios.post<TavilyResponse>(`${this.apiUrl}/search`, payload, {
        timeout: this.timeout,
        headers: { 'Content-Type': 'application/json' },
      });

      return { data: response.data.results || [], error: null };
    } catch (error) {
      console.error('[TavilyService] Search error:', error);

      let errorMessage = 'Failed to search web';

      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.response?.data) {
          const data = axiosError.response.data as { error?: string };
          errorMessage = data.error || errorMessage;
        } else if (axiosError.message) {
          errorMessage = axiosError.message;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      return {
        data: null,
        error: new Error(errorMessage),
      };
    }
  }

  /**
   * Search multiple queries in parallel
   * Useful for researching multiple questions simultaneously
   *
   * @param queries - Array of search queries
   * @returns Array of search results for each query
   */
  async searchMultiple(
    queries: string[],
    options?: {
      maxResultsPerQuery?: number;
      searchDepth?: 'basic' | 'advanced';
    }
  ): Promise<ResearchServiceResponse<TavilySearchResult[][]>> {
    try {
      // Preferred: batch via edge function
      const edgeRes = await this.invokeEdgeFunction<TavilySearchResult[][]>({
        mode: 'multi',
        queries,
        options: {
          maxResultsPerQuery: options?.maxResultsPerQuery ?? 3,
          searchDepth: options?.searchDepth ?? 'basic',
        },
      });
      if (!edgeRes.error && edgeRes.data) {
        return { data: edgeRes.data, error: null };
      }

      // Fallback: execute searches in parallel on client
      const searchPromises = queries.map(query =>
        this.search(query, {
          maxResults: options?.maxResultsPerQuery || 3,
          searchDepth: options?.searchDepth || 'basic',
        })
      );
      const results = await Promise.all(searchPromises);

      const errors = results.filter(r => r.error).map(r => r.error!.message);
      if (errors.length > 0) {
        return { data: null, error: new Error(`Some searches failed: ${errors.join(', ')}`) };
      }

      const allResults = results.map(r => r.data || []);
      return { data: allResults, error: null };
    } catch (error) {
      console.error('[TavilyService] Multiple search error:', error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error('Unknown error'),
      };
    }
  }

  /**
   * Convert Tavily results to ResearchSource format
   * Filters out low-quality sources and normalizes data
   *
   * @param results - Tavily search results
   * @param minScore - Minimum relevance score (0-1)
   * @returns Normalized research sources
   */
  convertToResearchSources(
    results: TavilySearchResult[],
    minScore: number = 0.5
  ): ResearchSource[] {
    return results
      .filter(result => result.score >= minScore)
      .map(result => ({
        url: result.url,
        title: result.title,
        summary: result.content,
        relevance: result.score,
        sourceName: this.extractDomainName(result.url),
      }))
      .sort((a, b) => b.relevance - a.relevance); // Sort by relevance
  }

  /**
   * Extract clean domain name from URL
   * e.g., "https://www.bloomberg.com/news/..." -> "Bloomberg"
   */
  private extractDomainName(url: string): string {
    try {
      const domain = new URL(url).hostname;
      // Remove www. and .com/.net/etc
      const parts = domain.replace('www.', '').split('.');
      const name = parts[0];
      // Capitalize first letter
      return name.charAt(0).toUpperCase() + name.slice(1);
    } catch {
      return 'Unknown';
    }
  }

  /**
   * Get recommended domains for financial research
   * These are high-quality sources for market/stock research
   */
  getFinancialDomains(): string[] {
    return [
      'bloomberg.com',
      'reuters.com',
      'wsj.com',
      'ft.com',
      'cnbc.com',
      'marketwatch.com',
      'seekingalpha.com',
      'fool.com',
      'barrons.com',
      'investing.com',
    ];
  }

  /**
   * Get domains to exclude (low-quality sources)
   */
  getExcludedDomains(): string[] {
    return ['reddit.com', 'twitter.com', 'facebook.com', 'youtube.com', 'tiktok.com'];
  }
}

// Export singleton instance
export const tavilyService = new TavilyService();
